import { getDateAsString } from '@/lib';
import { useOrganizations } from '@/pages/Organizations/hooks/useOrganizations';
import detailsPageStyles from '@/styles/components/details.page.module.css';
import type { UserPublic } from '@/types';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Switch } from '@nautilusbio/nb-design-system/Switch';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useForm } from '@tanstack/react-form';
import clsx from 'clsx';
import { Mail } from 'lucide-react';
import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import styles from './UserDetails.module.css';
import { inferPrimaryOrgId, inferPrimaryRole } from './useUserDetail';

export const userEditSchema = z.object({
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.string().email(),
  is_active: z.boolean(),
});

export type UserEditValues = z.infer<typeof userEditSchema>;

function useUserEditFormType() {
  return useForm({ defaultValues: {} as UserEditValues });
}
export type UserEditFormApi = ReturnType<typeof useUserEditFormType>;

type UserHeaderCardProps = {
  user: UserPublic;
  isEditing: boolean;
  form: UserEditFormApi;
};

export const UserHeaderCard: FC<UserHeaderCardProps> = ({ user, isEditing, form }) => {
  const { t: tUsers } = useTranslation('users');
  const { t: tGeneral } = useTranslation('general');
  const { data: orgs = [] } = useOrganizations();
  const orgNameMap = useMemo(() => new Map(orgs.map(o => [o.id, o.name])), [orgs]);

  const primaryOrgId = inferPrimaryOrgId(user);
  const primaryRole = primaryOrgId ? inferPrimaryRole(user, primaryOrgId) : undefined;
  const primaryOrgName = primaryOrgId ? orgNameMap.get(primaryOrgId) : undefined;

  return (
    <Stack
      alignItems="stretch"
      className={clsx(styles.headerCard, detailsPageStyles.header)}
      gap={6}
      orientation="horizontal"
    >
      {/* Left column */}
      <Stack className={styles.headerCardLeft} gap={3} orientation="vertical">
        {isEditing ? (
          <>
            <form.Field
              name="first_name"
              validators={{
                onChange: ({ value }) => {
                  const result = z.string().min(1).safeParse(value);
                  if (!result.success) return result.error.issues[0]?.message;
                  return undefined;
                },
              }}
            >
              {field => (
                <Input
                  className={styles.fieldRow}
                  data-testid="user-detail-first-name"
                  error={field.state.meta.errors[0]}
                  id="user-detail-first-name"
                  label={{ children: tGeneral('first_name'), id: 'user-fn-label', htmlFor: 'user-detail-first-name' }}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                  value={field.state.value}
                />
              )}
            </form.Field>

            <form.Field
              name="last_name"
              validators={{
                onChange: ({ value }) => {
                  const result = z.string().min(1).safeParse(value);
                  if (!result.success) return result.error.issues[0]?.message;
                  return undefined;
                },
              }}
            >
              {field => (
                <Input
                  className={styles.fieldRow}
                  data-testid="user-detail-last-name"
                  error={field.state.meta.errors[0]}
                  id="user-detail-last-name"
                  label={{ children: tGeneral('last_name'), id: 'user-ln-label', htmlFor: 'user-detail-last-name' }}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                  value={field.state.value}
                />
              )}
            </form.Field>

            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => {
                  const result = z.string().email().safeParse(value);
                  if (!result.success) return result.error.issues[0]?.message;
                  return undefined;
                },
              }}
            >
              {field => (
                <Input
                  className={styles.fieldRow}
                  data-testid="user-detail-email"
                  error={field.state.meta.errors[0]}
                  id="user-detail-email"
                  label={{ children: tGeneral('email'), id: 'user-email-label', htmlFor: 'user-detail-email' }}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                  type="email"
                  value={field.state.value}
                />
              )}
            </form.Field>
          </>
        ) : (
          <>
            <Text as="h1" className={styles.fullName} size="xl2">
              {user.full_name}
            </Text>

            <Stack alignItems="center" className={styles.fieldRow} gap={2} orientation="horizontal">
              <Mail size={14} />
              <a href={`mailto:${user.email}`}>
                <Text as="span">{user.email}</Text>
              </a>
            </Stack>

            <Stack gap={6} orientation="horizontal">
              <Stack orientation="vertical">
                <Text as="span" className={styles.fieldLabel}>
                  {tUsers('primary_role')}
                </Text>
                <Text as="span">{primaryRole ?? '—'}</Text>
              </Stack>

              <Stack orientation="vertical">
                <Text as="span" className={styles.fieldLabel}>
                  {tUsers('organization_column_header')}
                </Text>
                <Text as="span">{primaryOrgName ?? '—'}</Text>
              </Stack>
            </Stack>
          </>
        )}
      </Stack>

      {/* Right column — 2-col grid with vertical divider, matching Study details stats style */}
      <Grid
        className={styles.headerCardRight}
        gridTemplateColumns="1fr auto 1fr"
        gridTemplateRows="auto auto"
        justifyItems="center"
        rowGap={4}
      >
        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tGeneral('updated_at')}
          </Text>
          <Text as="span" noWrap size="lg">
            {user.updated_at ? getDateAsString(new Date(user.updated_at)) : '—'}
          </Text>
        </Stack>

        {/* Vertical divider — grid-column:2 grid-row:1/span 2 via CSS; auto-placement flows around it */}
        <div aria-hidden className={styles.statDivider} />

        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tGeneral('created_at')}
          </Text>
          <Text as="span" noWrap size="lg">
            {user.created_at ? getDateAsString(new Date(user.created_at)) : '—'}
          </Text>
        </Stack>

        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tUsers('status')}
          </Text>
          {isEditing ? (
            <form.Field name="is_active">
              {field => (
                <Switch
                  checked={field.state.value}
                  data-testid="user-detail-active"
                  label={field.state.value ? tUsers('status_active') : tUsers('status_inactive')}
                  onChange={checked => field.handleChange(checked)}
                />
              )}
            </form.Field>
          ) : (
            <Badge size="xs" variant={user.is_active ? 'success' : 'default'}>
              {user.is_active ? tUsers('status_active') : tUsers('status_inactive')}
            </Badge>
          )}
        </Stack>

        <Stack alignItems="center" gap={2} orientation="vertical">
          <Text as="span" className={styles.statLabel} noWrap>
            {tUsers('last_login_at_column_header')}
          </Text>
          <Text as="span" noWrap size="lg">
            {user.last_login_at ? getDateAsString(new Date(user.last_login_at)) : '—'}
          </Text>
        </Stack>
      </Grid>
    </Stack>
  );
};
